import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

const SkillsDiv = () => {
    useEffect(() => {
        // Initialize AOS for animations
        AOS.init({
            duration: 500, // Animation duration (in milliseconds)
            once: false, // Whether animation should happen only once
            mirror: false, // Whether elements should animate out while scrolling past them
        });
    }, []);

    // Dynamic properties
    const height = 60;
    const width = 60;
    const color = "default"; // Replace with your light mode color
    const darkModeColor = "white"; // Replace with your dark mode color

    const getIconUrl = (iconSlug, isDarkMode) => {
        return `https://cdn.simpleicons.org/${iconSlug}/${
            isDarkMode ? darkModeColor : color
        }`;
    };

    // Set this to determine if dark mode is active (for demonstration)
    const isDarkMode = false; // You can toggle this based on your app's state

    // Skill data (keeping your current data structure)
    const skills = [
        {
            category: "Frontend",
            subcategories: [
                {
                    subcategory: "Core Technologies",
                    items: [
                        { name: "HTML5", slug: "html5" },
                        { name: "CSS3", slug: "css3" },
                        { name: "JavaScript", slug: "javascript" },
                    ],
                },
                {
                    subcategory: "Frameworks & Libraries",
                    items: [
                        { name: "React", slug: "react" },
                        { name: "Redux", slug: "redux" },
                        { name: "Next.js", slug: "nextdotjs" }, // To learn
                    ],
                },
                {
                    subcategory: "Styling Tools",
                    items: [
                        { name: "Bootstrap", slug: "bootstrap" },
                        { name: "Tailwind CSS", slug: "tailwindcss" },
                        { name: "Material UI", slug: "mui" },
                        { name: "Chakra UI", slug: "chakraui" }, // To learn
                        { name: "SASS/SCSS", slug: "sass" }, // To learn
                    ],
                },
                {
                    subcategory: "Enhancements",
                    items: [
                        { name: "Framer Motion", slug: "framer" }, // Known
                        { name: "Swiper", slug: "swiper" }, // Known
                        { name: "Headless UI", slug: "headlessui" },
                    ],
                },
            ],
        },
        {
            category: "Backend",
            subcategories: [
                {
                    subcategory: "Core Technologies",
                    items: [
                        { name: "Node.js", slug: "nodedotjs" },
                        { name: "Express", slug: "express" },
                    ],
                },
                {
                    subcategory: "Databases",
                    items: [
                        { name: "MongoDB", slug: "mongodb" },
                        { name: "PostgreSQL", slug: "postgresql" }, // To learn
                    ],
                },
                {
                    subcategory: "Utilities & Security",
                    items: [
                        { name: "JWT", slug: "jsonwebtokens" },
                        { name: "dotenv", slug: "dotenv" }, // Known
                        { name: "GraphQL", slug: "graphql" }, // To learn
                    ],
                },
                {
                    subcategory: "Data Management",
                    items: [
                        { name: "JSON", slug: "json" },
                        { name: "Mongoose", slug: "mongoose" },
                    ],
                },
            ],
        },
        {
            category: "DevOps & Cloud",
            subcategories: [
                {
                    subcategory: "Cloud Services",
                    items: [
                        { name: "Docker", slug: "docker" },
                        { name: "Vercel", slug: "vercel" },
                        { name: "Google Cloud", slug: "googlecloud" },
                        { name: "AWS", slug: "amazonwebservices" }, // To learn
                        { name: "Render", slug: "render" }, // To learn
                    ],
                },
                {
                    subcategory: "Deployment & Infrastructure",
                    items: [
                        { name: "GitHub Pages", slug: "githubpages" }, // Known
                        { name: "Terraform", slug: "terraform" }, // To learn
                        { name: "Netlify", slug: "netlify" }, // To learn
                    ],
                },
            ],
        },
        {
            "category": "Linux & OS",
            "subcategories": [
                {
                    "subcategory": "Linux Distributions",
                    "items": [
                        { "name": "Linux", "slug": "linux" }, // To learn
                        { "name": "Debian", "slug": "debian" }, // To learn
                        { "name": "Kali Linux", "slug": "kalilinux" }, // To learn
                        { "name": "Void Linux", "slug": "voidlinux" }, // To learn
                        { "name": "Arch Linux", "slug": "archlinux" }, // To learn
                        { "name": "Linux Mint", "slug": "linuxmint" } // To learn
                    ]
                },
                {
                    "subcategory": "Shells",
                    "items": [
                        { "name": "Fish", "slug": "fishshell" }, // To learn
                        { "name": "Zsh", "slug": "zsh" }, // To learn
                        { "name": "GNU Bash", "slug": "gnubash" } // Known
                    ]
                }
            ]
        },
        {
            category: "Programming Languages",
            subcategories: [
                {
                    subcategory: "Primary Languages",
                    items: [
                        { name: "C", slug: "c" },
                        { name: "Python", slug: "python" },
                    ],
                },
                {
                    subcategory: "Supersets & Transpilers",
                    items: [
                        { name: "C++", slug: "cplusplus" },
                        { name: "TypeScript", slug: "typescript" },
                    ],
                },
            ],
        },
        {
            category: "Testing & Performance",
            subcategories: [
                {
                    subcategory: "Testing Tools",
                    items: [
                        { name: "Cypress", slug: "cypress" }, // To learn
                        { name: "Jest", slug: "jest" }, // Known
                    ],
                },
                {
                    subcategory: "Performance Tools",
                    items: [
                        { name: "Lighthouse", slug: "lighthouse" }, // To learn
                    ],
                },
            ],
        },
        {
            category: "Creative Software",
            subcategories: [
                {
                    subcategory: "Video Editing",
                    items: [
                        { name: "Premiere Pro", slug: "adobepremierepro" }, // To learn
                        { name: "Vegas Pro", slug: "vegas" }, // To learn
                    ],
                },
                {
                    subcategory: "Design & Animation",
                    items: [
                        { name: "After Effects", slug: "adobeaftereffects" }, // To learn
                        { name: "Blender", slug: "blender" }, // To learn
                        { name: "Figma", slug: "figma" }, // To learn
                    ],
                },
            ],
        },
        {
            category: "Game Development",
            subcategories: [
                {
                    subcategory: "Game Engines",
                    items: [
                        { name: "Unity", slug: "unity" }, // To learn
                        { name: "Unreal Engine", slug: "unrealengine" }, // To learn
                    ],
                },
            ],
        },
    ];

    return (
        <>
            <div className="p-4 container mx-auto rounded-lg shadow ubuntu-regular">
                <h2 className="text-3xl font-bold mb-8 text-left p-4">
                    Skills
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 gap-y-20">
                    {skills.map((skillCategory, index) => (
                        <div key={index} className="p-4">
                            <div
                                className="flex flex-col items-center"
                                data-aos="fade-up"
                            >
                                <h2 className="text-2xl mb-4">
                                    {skillCategory.category}
                                </h2>
                            </div>
                            {skillCategory.subcategories.map(
                                (subcategory, subIndex) => (
                                    <div key={subIndex} className="mb-6 ">
                                        <h3
                                            className="text-lg font-semibold text-yellow-400 my-4 text-center"
                                            data-aos="fade-in"
                                        >
                                            {subcategory.subcategory}
                                        </h3>
                                        <div
                                            className="grid grid-cols-3 gap-8 p-4 container mx-auto"
                                            style={{ gridAutoRows: "1fr" }}
                                        >
                                            {subcategory.items.map(
                                                (skill, idx) => (
                                                    <div
                                                        key={idx}
                                                        data-aos="zoom-in-up"
                                                        data-aos-delay={
                                                            idx * 50
                                                        } // Stagger animation for each icon
                                                        className="flex flex-col items-center justify-center "
                                                    >
                                                        <div className="transition-transform hover:scale-110 flex flex-col items-center justify-center">
                                                            <img
                                                                height={height}
                                                                width={width}
                                                                src={getIconUrl(
                                                                    skill.slug,
                                                                    isDarkMode
                                                                )}
                                                                alt={skill.name}
                                                            />
                                                            <span className="mt-2 text-sm text-center text-gray-400">
                                                                {skill.name}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default SkillsDiv;
